import { GridColDef } from '@mui/x-data-grid';
import { getDateUsingFormat } from 'wisestep-base';

import { LeadWidget } from '../../DerivedCharts/helper';
import { renderCellFor } from '../helper';
import { ILeadDTO } from '../jobs/types/postgreJobsDTO';

export const getLeadTableColumns = (
  widgetName: LeadWidget,
  widgetData: ILeadDTO,
  leadStatusLabel?: string
): GridColDef[] => {
  const column = {
    COUNT_ALL: {
      field: 'count',
      headerName: `Total (${widgetData.count ?? 0})`,
      minWidth: 120,
      flex: 1,
    },
    COUNT_STATUS: (leadStatus: string) => ({
      field: 'countByStatus',
      headerName: (() => {
        const countByStatusArray =
          widgetData?.resultSet?.map((record) => record.countByStatus?.[leadStatus] ?? 0) ?? [];
        return `${leadStatus} (${countByStatusArray.reduce((a, b) => a + b, 0)})`;
      })(),
      minWidth: 130,
      flex: 1,
      valueGetter: (params) => params.row.countByStatus?.[leadStatus] ?? 0,
    }),
    COUNT_PERCENTAGE: (leadStatus: string) => ({
      field: 'perc-field',
      headerName: 'Percentage (%)',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => Math.round((params.value ?? 0) * 100) + ' %',
      valueGetter: (params) => (params.row.countByStatus?.[leadStatus] ?? 0) / params.row.count,
    }),
    CLIENT: {
      field: 'clientName',
      headerName: 'Client Name',
      minWidth: 120,
      flex: 1.5,
      renderCell: (params) => renderCellFor('CLIENT', params),
    },
    CLIENT_CONTACT: {
      field: 'clientRecruiterName',
      headerName: 'Client SPOC',
      minWidth: 160,
      flex: 1.5,
      renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
    },
    LEAD_OWNER: {
      field: 'leadOwnerName',
      headerName: 'Lead Owner',
      minWidth: 160,
      flex: 1.5,
      renderCell: (params) => renderCellFor('RECRUITER_ID', params, undefined, 'leadOwnerName'),
    },
    LEAD_CREATOR: {
      field: 'leadCreatorName',
      headerName: 'User',
      minWidth: 160,
      flex: 1.5,
      renderCell: (params) => renderCellFor('RECRUITER_ID', params, undefined, 'leadCreatorName'),
    },
    LEAD_CREATED_DATE: {
      field: 'createdOn',
      headerName: 'Created Date',
      width: 130,
      renderCell: (params) => (params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : ''),
    },
    LEAD_MODIFIED_DATE: {
      field: 'modifiedOn',
      headerName: 'Updated Date',
      width: 130,
      renderCell: (params) => (params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : ''),
    },
  };

  switch (widgetName) {
    case LeadWidget.TABLE_CREATED_BY_STATUS: {
      const leadStatus = `${leadStatusLabel ?? 'Status'}`;
      return [column.LEAD_CREATOR, column.COUNT_STATUS(leadStatus)];
    }
    case LeadWidget.TABLE_OWNED_BY_STATUS: {
      const leadStatus = `${leadStatusLabel ?? 'Status'}`;
      return [
        column.LEAD_OWNER,
        column.COUNT_ALL,
        column.COUNT_STATUS(leadStatus),
        column.COUNT_PERCENTAGE(leadStatus),
      ];
    }
    case LeadWidget.TABLE_CREATED:
      return [column.LEAD_CREATOR, column.COUNT_ALL];
    case LeadWidget.TABLE_OWNED:
      return [column.LEAD_OWNER, column.COUNT_ALL];
    case LeadWidget.TABLE_PIPELINE:
      return [
        column.CLIENT,
        column.CLIENT_CONTACT,
        column.LEAD_OWNER,
        column.LEAD_CREATOR,
        column.LEAD_CREATED_DATE,
        column.LEAD_MODIFIED_DATE,
      ];
    default:
      throw new Error('Invalid table widget name');
  }
};

export const defaultLeadWidgetData: ILeadDTO = {
  count: 0,
  countByStatus: {},
  resultSet: [],
};
